/**
 * Severity Change Dropdown Component
 *
 * Provides consistent "Change Severity" dropdown for vulnerability severity changes with:
 * - Critical, High, Medium, Low severity options
 * - Inline dropdown without modal
 * - Dynamic severity options based on configuration
 *
 * Migrated from inline Dropdown in VulnerabilityActions to standard action pattern.
 *
 * @example
 * ```tsx
 * <SeverityChangeDropdown
 *   risks={selectedVulnerabilities}
 *   severityOptions={severityFilterOptions}
 *   onSeverityChange={handleSeverityChange}
 *   disabled={false}
 * />
 * ```
 *
 * @module components/actions
 */

import { ACTION_ICONS } from '@/components/actions/constants/actionIcons';
import { Dropdown } from '@/components/Dropdown';
import type { Severity } from '@/types/dropdown.types';

/**
 * Props for SeverityChangeDropdown component
 *
 * @template T - Type of risk items (must have key and status)
 */
export interface SeverityChangeDropdownProps<
  T extends { key: string; status: string } = { key: string; status: string },
> {
  /** Risks to change severity for */
  risks: T[];
  /** Severity options for dropdown */
  severityOptions: Array<{
    label: string;
    value: string;
    id: string;
    severity?: string;
  }>;
  /** Handler for severity change */
  onSeverityChange: (option: {
    value?: string | number;
    label: string;
    severity?: string;
  }) => void;
  /** Disable the dropdown */
  disabled?: boolean;
  /** Hide label (for compact views) */
  hideLabel?: boolean;
  /** Test ID for dropdown */
  'data-testid'?: string;
}

/**
 * Severity Change Dropdown Component
 *
 * Renders a dropdown with severity change options for vulnerabilities.
 * Changes apply immediately to all selected risks.
 *
 * @template T - Type of risk items (must have key and status)
 * @param props - Component props
 * @returns Dropdown component
 */
export function SeverityChangeDropdown<
  T extends { key: string; status: string },
>({
  risks: _risks,
  severityOptions,
  onSeverityChange,
  disabled = false,
  hideLabel = false,
  'data-testid': testId = 'severity-change-dropdown',
}: SeverityChangeDropdownProps<T>) {
  return (
    <div data-testid={testId} className="w-fit">
      <Dropdown
        placeholder={hideLabel ? undefined : 'Change Severity'}
        startIcon={<ACTION_ICONS.SEVERITY_CHANGE className="size-4" />}
        styleType="rounded"
        size="sm"
        disabled={disabled}
        className="[&_span:nth-child(3)]:hidden"
        menu={{
          items: severityOptions.map(opt => ({
            value: opt.value,
            label: opt.label,
            disabled: disabled,
            severity: opt.label.toLowerCase() as Severity,
            onClick: () => onSeverityChange(opt),
          })),
          placement: 'bottom-start',
        }}
      />
    </div>
  );
}
